// AI activity: everything the TearDrop Assistant (the local AI on server3) has
// added or tried to add, with one-click undo for what it added; its settings
// and tokens; and the log of every request made with those tokens. The open
// tab lives in the URL (?tab=settings) so a reload keeps it.
//
// The assistant only adds (garageFarmServer routes/assistant.js): each add is
// confirmed with Michael in the chat first, and undo here removes it again.
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, API_BASE } from '../lib/auth';
import { Button, Checkbox, Input, PageHeader, Pill, Select } from '../lib/ui';
import { formatWhen } from '../lib/briefings';
import { FILTERS, KIND_LABEL, STATUS, TOKEN_KINDS, WHERE, filterActions, undoProblem } from '../lib/aiActivity';
import { useFlash, errorText, Label } from '../components/TradeCommon';
import '../components/Trade.css';
import '../components/Briefings.css';

const TABS = [
  { key: 'activity', label: 'Activity' },
  { key: 'settings', label: 'Settings and tokens' },
  { key: 'requests', label: 'Requests' },
];

const SUBTITLES = {
  activity: 'What the assistant has added, each one confirmed with you first. Undo removes it again',
  settings: 'Switch the assistant\'s adds on or off, where reminders go, and the tokens its tools use',
  requests: 'Every request made with the assistant\'s tokens, including refused ones',
};

// ── Activity ──────────────────────────────────────────────────────────────────

function ActionRow({ a, onUndo, busy }) {
  const status = STATUS[a.status] || { label: a.status };
  const where = WHERE[a.kind];
  return (
    <div className="br-card" style={{ padding: '12px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Pill tone={status.tone}>{status.label}</Pill>
        <strong>{KIND_LABEL[a.kind] || a.kind}</strong>
        {a.target?.label && a.status !== 'undone' && <span>{a.target.label}</span>}
        <span className="muted" style={{ marginLeft: 'auto', fontSize: 13 }}>
          {formatWhen(a.confirmedAt || a.createdAt)}{a.undoneAt ? `, undone ${formatWhen(a.undoneAt)}` : ''}
        </span>
        {a.status === 'done' && (
          <Button size="sm" variant="danger" onClick={() => onUndo(a)} disabled={busy}>Undo</Button>
        )}
      </div>
      <div style={{ marginTop: 6, fontSize: 14 }}>{a.summary}</div>
      {a.request && <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>You asked: "{a.request}"</div>}
      {a.warnings?.length > 0 && <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>Noted: {a.warnings.join(' ')}</div>}
      {a.error && <div style={{ marginTop: 4, fontSize: 13, color: 'var(--bad-fg)' }}>{a.error}</div>}
      {a.status === 'done' && where && (
        <div style={{ marginTop: 4, fontSize: 13 }}><Link to={where.to}>{where.label}</Link></div>
      )}
    </div>
  );
}

function ActivityTab({ flash }) {
  const [actions, setActions] = useState(null);
  const [filter, setFilter] = useState('added');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/assistant/activity', { params: { limit: 300 } });
      setActions(data.actions);
    } catch (e) { flash(errorText(e), 'bad'); }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const undo = async (a) => {
    if (!window.confirm(`Undo this ${(KIND_LABEL[a.kind] || 'add').toLowerCase()}?\n\n${a.summary}`)) return;
    setBusy(true);
    try {
      let res;
      try {
        res = await api.post(`/assistant/activity/${a._id}/undo`);
      } catch (e) {
        const p = undoProblem(e);
        if (p.kind !== 'changed') throw e;
        if (!window.confirm(p.message)) { setBusy(false); return; }
        res = await api.post(`/assistant/activity/${a._id}/undo`, { force: true });
      }
      flash(res.data.message || 'Undone');
      await load();
    } catch (e) {
      flash(undoProblem(e).message, 'bad');
    }
    setBusy(false);
  };

  if (!actions) return <p className="br-card__empty">Loading…</p>;
  const shown = filterActions(actions, filter);
  const counts = Object.fromEntries(FILTERS.map(f => [f.key, filterActions(actions, f.key).length]));

  return (
    <div>
      <div className="br-toolbar">
        <Select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 'auto' }}>
          {FILTERS.map(f => <option key={f.key} value={f.key}>{f.label} ({counts[f.key]})</option>)}
        </Select>
        <Button onClick={load}>Refresh</Button>
      </div>
      {shown.length === 0 && (
        <p className="br-card__empty">
          {filter === 'added' ? 'The assistant hasn\'t added anything yet.' : 'Nothing here.'}
        </p>
      )}
      {shown.map(a => <ActionRow key={a._id} a={a} onUndo={undo} busy={busy} />)}
    </div>
  );
}

// ── Settings and tokens ───────────────────────────────────────────────────────

const QUICK_NOTE_URL = `${API_BASE}/notes/quick`;

function TokenBox({ kind, info, onChange, flash }) {
  const [token, setToken] = useState('');
  const meta = TOKEN_KINDS[kind];

  const make = async () => {
    if (info.exists && !window.confirm('Make a new token? The old one stops working straight away.')) return;
    try {
      const { data } = await api.post(`/assistant/tokens/${kind}`);
      setToken(data.token);
      onChange(data.settings);
    } catch (e) { flash(errorText(e), 'bad'); }
  };
  const revoke = async () => {
    if (!window.confirm('Revoke this token? Anything using it stops working.')) return;
    try {
      const { data } = await api.delete(`/assistant/tokens/${kind}`);
      setToken('');
      onChange(data.settings);
    } catch (e) { flash(errorText(e), 'bad'); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(token); flash('Token copied'); } catch { flash('Copy it by hand: select the text', 'warn'); }
  };

  return (
    <>
      <h4>{meta.title}</h4>
      <div className="trade-form__hint">{meta.what}</div>
      <div className="trade-form__hint">{meta.where}</div>
      {kind === 'notes' && (
        <div className="trade-form__hint">
          <span className="br-code">POST {QUICK_NOTE_URL}</span> with <span className="br-code">{'{"text": "…"}'}</span>
        </div>
      )}
      <div className="trade-form__hint">
        {info.exists
          ? `Token made ${formatWhen(info.createdAt)}${info.lastUsedAt ? `, last used ${formatWhen(info.lastUsedAt)}` : ', not used yet'}.`
          : 'No token yet.'}
      </div>
      {token && (
        <div>
          <div className="br-token">{token}</div>
          <div className="trade-form__hint" style={{ marginTop: 6 }}>Copy it now: it isn't shown again. <Button size="sm" onClick={copy}>Copy</Button></div>
        </div>
      )}
      <div className="br-toolbar" style={{ margin: 0 }}>
        <Button onClick={make}>{info.exists ? 'Make a new token' : 'Make a token'}</Button>
        {info.exists && <Button variant="danger" onClick={revoke}>Revoke</Button>}
      </div>
    </>
  );
}

function SettingsTab({ flash }) {
  const [settings, setSettings] = useState(null);
  const [topic, setTopic] = useState('');

  useEffect(() => {
    api.get('/assistant/settings')
      .then(({ data }) => { setSettings(data.settings); setTopic(data.settings.reminderTopic); })
      .catch(e => flash(errorText(e), 'bad'));
  }, [flash]);

  if (!settings) return <p className="br-card__empty">Loading…</p>;

  const save = async (patch, done) => {
    try {
      const { data } = await api.patch('/assistant/settings', patch);
      setSettings(data.settings);
      setTopic(data.settings.reminderTopic);
      flash(done);
    } catch (e) { flash(errorText(e), 'bad'); }
  };
  const shownTopic = topic || settings.briefingTopic;

  return (
    <div className="br-settings">
      <div className="trade-form">
        <h4>Adds</h4>
        <Checkbox
          checked={settings.writesEnabled}
          onChange={(e) => save({ writesEnabled: e.target.checked }, e.target.checked ? 'The assistant can add things again' : 'The assistant can only look things up now')}
          label="Let the assistant add things (each one is confirmed with you first)"
        />
        <div className="trade-form__hint">Off: it can still look things up (deliveries, stock, sensors, prices) but can't add anything.</div>

        <h4>Reminders</h4>
        <div>
          <Label hint="on push.teardrophydro.co.uk">ntfy topic</Label>
          <div className="br-times">
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={settings.briefingTopic} />
            <Button onClick={() => save({ reminderTopic: topic.trim() }, 'Saved')} disabled={topic === settings.reminderTopic}>Save</Button>
          </div>
          <div className="trade-form__hint" style={{ marginTop: 6 }}>
            Empty uses the briefings' topic, <b>{settings.briefingTopic}</b>. A new topic needs, once on server2, <span className="br-code">sudo ntfy access admin {shownTopic} rw</span> and subscribing to <b>{shownTopic}</b> in the ntfy app. Customer names, numbers and addresses are masked in the push.
          </div>
        </div>

        <TokenBox kind="actions" info={settings.tokens.actions} onChange={setSettings} flash={flash} />
        <TokenBox kind="notes" info={settings.tokens.notes} onChange={setSettings} flash={flash} />
      </div>
    </div>
  );
}

// ── Requests ──────────────────────────────────────────────────────────────────

function RequestsTab({ flash }) {
  const [log, setLog] = useState(null);
  useEffect(() => {
    api.get('/assistant/log', { params: { limit: 200 } })
      .then(({ data }) => setLog(data.log))
      .catch(e => flash(errorText(e), 'bad'));
  }, [flash]);
  if (!log) return <p className="br-card__empty">Loading…</p>;
  if (!log.length) return <p className="br-card__empty">No requests yet.</p>;
  return (
    <div className="ui-card" style={{ padding: 0, overflowX: 'auto' }}>
      <table className="trade-table">
        <thead><tr><th>When</th><th>Token</th><th>Request</th><th>What</th><th>Result</th><th>Problem</th></tr></thead>
        <tbody>
          {log.map(l => (
            <tr key={l._id}>
              <td>{formatWhen(l.at)}</td>
              <td>{l.token || 'none'}</td>
              <td><span className="br-code">{l.method} {l.path.replace(/^\/api/, '')}</span></td>
              <td>{KIND_LABEL[l.kind] || l.kind}</td>
              <td><Pill tone={l.status < 300 ? 'ok' : l.status < 500 ? 'warn' : 'bad'}>{l.status}</Pill></td>
              <td className="muted">{l.error}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AiActivityPage() {
  const [params, setParams] = useSearchParams();
  const keys = TABS.map(t => t.key);
  const tab = keys.includes(params.get('tab')) ? params.get('tab') : 'activity';
  const { flash, banner } = useFlash();
  const onTabChange = (t) => setParams(t === 'activity' ? {} : { tab: t }, { replace: true });

  return (
    <div className="dashboard-container">
      <PageHeader title="AI activity" subtitle={SUBTITLES[tab]} tabs={TABS} activeTab={tab} onTabChange={onTabChange} />
      {banner}
      {tab === 'activity' && <ActivityTab flash={flash} />}
      {tab === 'settings' && <SettingsTab flash={flash} />}
      {tab === 'requests' && <RequestsTab flash={flash} />}
    </div>
  );
}
